import { useTranslation } from 'react-i18next';
import type { PlateStyle } from '../../services/licensePlate';
import './LicensePlate.css';

// Círculo de 12 estrellas de la bandera europea, a la escala de la banda.
// Coordenadas precalculadas una sola vez (radio 7), no en cada render.
const STAR = 'M0,-1.6L0.38,-0.52L1.52,-0.49L0.61,0.2L0.94,1.29L0,0.64L-0.94,1.29L-0.61,0.2L-1.52,-0.49L-0.38,-0.52Z';
const STAR_POSITIONS = Array.from({ length: 12 }, (_, i) => {
  const a = (i * Math.PI) / 6;
  return [Math.round(Math.sin(a) * 700) / 100, Math.round(-Math.cos(a) * 700) / 100] as const;
});

function EuStars() {
  return (
    <svg className="license-plate__stars" viewBox="-9 -9 18 18" aria-hidden="true" focusable="false">
      {STAR_POSITIONS.map(([x, y], i) => (
        <path key={i} d={STAR} transform={`translate(${x} ${y}) scale(1.2)`} />
      ))}
    </svg>
  );
}

// Nombre del país en el idioma del usuario ("Argentina", "Rumanía"), para el
// lector de pantalla y el tooltip. null si el navegador no sabe nombrarlo.
function countryDisplayName(iso: string | null, lang: string): string | null {
  if (!iso) return null;
  try {
    return new Intl.DisplayNames([lang], { type: 'region' }).of(iso.toUpperCase()) ?? null;
  } catch {
    return null;
  }
}

// Iconos pequeños para distinguir la matrícula del camión de la del remolque
// (los mismos que la app hermana).
type Vehicle = 'truck' | 'trailer';
const VEHICLE_ICON: Record<Vehicle, string> = {
  truck: 'M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z',
  trailer: 'M2 5h15v10H2zM17 11.5h5v1.5h-5zM6.5 15.5a2 2 0 1 0 0.01 0zM11.5 15.5a2 2 0 1 0 0.01 0z',
};
const VEHICLE_LABEL_KEY: Record<Vehicle, string> = {
  truck: 'detail.plateTruck',
  trailer: 'detail.plateTrailer',
};

/**
 * Matrícula (camión o remolque) con el aspecto de la placa real de su país (ver
 * services/licensePlate). Mismo alto que la placa anterior para no mover la
 * tarjeta.
 */
export function LicensePlate({ number, style, vehicle }: {
  number?: string | null;
  style: PlateStyle;
  /** Con él, un icono delante indica si es la del camión o la del remolque. */
  vehicle?: Vehicle;
}) {
  const plate = <Plate number={number} style={style} vehicle={vehicle} />;
  if (!vehicle) return plate;
  return (
    <span className="license-plate-group">
      <svg className="license-plate-group__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d={VEHICLE_ICON[vehicle]} />
      </svg>
      {plate}
    </span>
  );
}

function Plate({ number, style, vehicle }: {
  number?: string | null;
  style: PlateStyle;
  vehicle?: Vehicle;
}) {
  const { t, i18n } = useTranslation();
  const value = (number ?? '').trim();
  const country = countryDisplayName(style.iso, i18n.language);
  const label = t(vehicle ? VEHICLE_LABEL_KEY[vehicle] : 'detail.platePlate', { value: value || t('detail.plateUnassigned') })
    + (country ? ` (${country})` : '');
  const captioned = style.kind === 'mercosur' || style.kind === 'black' || style.kind === 'caption';
  const modifiers = [
    `license-plate--${style.kind}`,
    style.kind === 'eu' && style.yellow ? 'license-plate--yellow' : '',
  ].filter(Boolean).join(' ');

  return (
    <span className={`license-plate ${modifiers}`} role="img" aria-label={label} title={label}>
      {(style.kind === 'eu' || style.kind === 'band') && (
        <span className="license-plate__band" aria-hidden="true">
          {style.kind === 'eu' && <EuStars />}
          <span className={`license-plate__code${style.code.length > 2 ? ' license-plate__code--long' : ''}`}>
            {style.code}
          </span>
        </span>
      )}
      {captioned ? (
        <span className="license-plate__body" aria-hidden="true">
          <span className="license-plate__caption">{style.caption}</span>
          <span className="license-plate__number">{value || '—'}</span>
        </span>
      ) : (
        <span className="license-plate__number" aria-hidden="true">{value || '—'}</span>
      )}
      {style.kind === 'eu' && style.rightBand && <span className="license-plate__band-right" aria-hidden="true" />}
    </span>
  );
}
